import React, { useEffect, useMemo, useState } from 'react';
import { type Dayjs } from 'dayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Box, Dialog, DialogContent, IconButton, MenuItem, TextField, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import { useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import type { SocialPlatform } from '../../../types/instagramAutomation.types';
import {
    BRAND_LUXE_MUTED,
    BRAND_LUXE_PRIMARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGH,
    BRAND_SURFACE_CONTAINER_LOW,
    brandModalOutlinedInputSx,
} from '../brandDashboardTokens';
import { ManageGoldButton } from './manageShared';
import { uploadUserMediaLibraryImage } from '../../../redux/userMedia/actions';
import type { IUploadUserMediaLibraryImageResult } from '../../../redux/userMedia/actions';
import { activeBrandDashboardBrandSelector } from '../../../redux/buzzbridge/selectors';
import { brandOpsIsBrandAdminSelector, brandOpsSettingsSelector } from '../../../redux/brandOps/selectors';
import { showToastMessage, useDispatchPromise } from '../../../utils/AppUtils';
import { PlatformComposeFields } from '../../../components/InstagramAutomation/PlatformComposeFields';
import {
    defaultTiktokFields,
    defaultYoutubeFields,
    type TiktokPostFields,
    type YoutubePostFields,
} from '../../../components/InstagramAutomation/platformPostFields';
import ComposeMediaPicker, { type PickedAsset } from './ComposeMediaPicker';
import ComposePhonePreview from './ComposePhonePreview';
import {
    PLATFORM_FORMATS,
    PLATFORM_LABEL,
    buildPlatformFields,
    defaultAspectFor,
    isVideoFormat,
    maxSlidesFor,
    mediaTypeFor,
    validatePlatformFields,
    type ComposeAspect,
    type ComposeFormat,
    type ComposeSlide,
    type InstagramComposeResult,
} from './composeShared';

export type { ComposeAspect, InstagramComposeResult } from './composeShared';

export type ComposeInitialAsset = {
    file_key?: string | null;
    preview_url?: string | null;
    title?: string | null;
    project_id?: string | null;
};

type Props = {
    open: boolean;
    onClose: () => void;
    onSubmit?: (result: InstagramComposeResult) => void | Promise<void>;
    /** Pre-seed the modal with a UGC asset when opening from the Content Library. */
    initialAsset?: ComposeInitialAsset | null;
};

const PLATFORMS: SocialPlatform[] = ['instagram', 'tiktok', 'youtube'];

const chipSx = (active: boolean) => ({
    border: 0,
    cursor: 'pointer',
    px: 1.5,
    py: 0.75,
    borderRadius: 2,
    fontSize: 12,
    fontFamily: FONT_ENUM.MAIN_MEDIUM,
    bgcolor: active ? BRAND_SURFACE_CONTAINER_HIGH : 'transparent',
    color: active ? BRAND_LUXE_PRIMARY : BRAND_LUXE_MUTED,
});

// ── Dark-themed picker popup styles ──────────────────────────────────────────
const pickerPaperSx = {
    bgcolor: '#1a1919',
    border: '1px solid rgba(255,255,255,0.10)',
    borderRadius: 2,
    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
    color: '#fff',
    '& .MuiPickersCalendarHeader-label': { color: '#fff' },
    '& .MuiDayCalendar-weekDayLabel': { color: BRAND_LUXE_MUTED },
    '& .MuiPickersYear-yearButton': { color: BRAND_LUXE_MUTED },
    '& .MuiClock-pin, & .MuiClockPointer-root': { bgcolor: BRAND_LUXE_PRIMARY },
    '& .MuiClockPointer-thumb': {
        borderColor: BRAND_LUXE_PRIMARY,
        bgcolor: BRAND_LUXE_PRIMARY,
    },
    '& .MuiClock-clock': { bgcolor: '#111' },
    '& .MuiClockNumber-root': { color: BRAND_LUXE_MUTED },
    '& .MuiPickersToolbar-root': { bgcolor: '#131313', color: '#fff' },
    '& .MuiPickersToolbarText-root': { color: '#fff' },
};

const pickerDaySx = {
    color: '#fff',
    '&:hover': { bgcolor: 'rgba(255,201,101,0.12)' },
    '&.Mui-selected': {
        bgcolor: `${BRAND_LUXE_PRIMARY} !important`,
        color: '#1a1919',
        fontWeight: 700,
    },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.2)' },
    '&.MuiPickersDay-today': {
        borderColor: BRAND_LUXE_PRIMARY,
    },
};

const pickerTimeItemSx = {
    color: BRAND_LUXE_MUTED,
    borderRadius: 1,
    '&:hover': { bgcolor: 'rgba(255,201,101,0.10)', color: '#fff' },
    '&.Mui-selected': {
        bgcolor: 'rgba(255,201,101,0.18) !important',
        color: `${BRAND_LUXE_PRIMARY} !important`,
        fontWeight: 700,
    },
};

const pickerActionBarSx = {
    '& .MuiButton-root': {
        color: BRAND_LUXE_PRIMARY,
        '&:hover': { bgcolor: 'rgba(255,201,101,0.08)' },
    },
};
// ─────────────────────────────────────────────────────────────────────────────

/** Multi-platform (IG / TikTok / YouTube) compose modal for the brand content calendar. */
export default function InstagramComposeModal({ open, onClose, onSubmit, initialAsset }: Props) {
    const dispatchP = useDispatchPromise();
    const activeBrand = useSelector(activeBrandDashboardBrandSelector);
    const isBrandAdmin = useSelector(brandOpsIsBrandAdminSelector);
    const brandSettings = useSelector(brandOpsSettingsSelector);
    const canPostNow = isBrandAdmin || !brandSettings?.require_brand_approval;
    const [platform, setPlatform] = useState<SocialPlatform>('instagram');
    const [format, setFormat] = useState<ComposeFormat>('carousel');
    const [aspect, setAspect] = useState<ComposeAspect>('4:5');
    const [caption, setCaption] = useState('');
    const [schedDate, setSchedDate] = useState<Dayjs | null>(null);
    const [schedTime, setSchedTime] = useState<Dayjs | null>(null);
    const scheduledAt =
        schedDate && schedTime
            ? schedDate.hour(schedTime.hour()).minute(schedTime.minute()).second(0).toISOString()
            : null;
    const [slides, setSlides] = useState<ComposeSlide[]>([]);
    const [project, setProject] = useState<{ id: string; preview: string; title: string } | null>(null);
    const [previewIdx, setPreviewIdx] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [postNowConfirmOpen, setPostNowConfirmOpen] = useState(false);
    const [yt, setYt] = useState<YoutubePostFields>(() => defaultYoutubeFields());
    const [tt, setTt] = useState<TiktokPostFields>(() => defaultTiktokFields());

    const isVideo = isVideoFormat(format);
    const maxSlides = maxSlidesFor(format);
    const hasMedia = slides.length > 0 || !!project;

    const previews = useMemo(() => {
        const list = slides.map(s => s.preview).filter(Boolean);
        return list.length ? list : project?.preview ? [project.preview] : [];
    }, [slides, project]);

    const clearMedia = () => {
        setSlides([]);
        setProject(null);
        setPreviewIdx(0);
    };

    // When modal opens with a pre-selected UGC asset, seed it into the compose state
    useEffect(() => {
        if (!open || !initialAsset) return;
        clearMedia();
        if (initialAsset.project_id) {
            setProject({
                id: initialAsset.project_id,
                preview: initialAsset.preview_url || '',
                title: initialAsset.title || '',
            });
        } else if (initialAsset.file_key) {
            setSlides([{ preview: initialAsset.preview_url || '', fileKey: initialAsset.file_key }]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, initialAsset]);

    const changePlatform = (p: SocialPlatform) => {
        const f = PLATFORM_FORMATS[p][0].id;
        setPlatform(p);
        setFormat(f);
        setAspect(defaultAspectFor(f));
        clearMedia();
    };

    const changeFormat = (f: ComposeFormat) => {
        setFormat(f);
        setAspect(defaultAspectFor(f));
        if (f !== 'carousel') setSlides(s => s.slice(0, 1));
    };

    const addSlide = (slide: ComposeSlide) => {
        setProject(null);
        setSlides(prev => (maxSlides === 1 ? [slide] : [...prev, slide].slice(0, maxSlides)));
    };

    const onFiles = (files: FileList | null) => {
        if (!files?.length) return;
        const images = Array.from(files).filter(f => f.type.startsWith('image/'));
        if (!images.length) {
            showToastMessage('Only image files can be uploaded here — use Library or Projects for video', 'error');
            return;
        }
        images.slice(0, Math.max(maxSlides - slides.length, 1)).forEach(file => {
            const reader = new FileReader();
            reader.onload = () => addSlide({ file, preview: String(reader.result || '') });
            reader.readAsDataURL(file);
        });
    };

    const pickAsset = (asset: PickedAsset) => {
        if (!asset.file_key) {
            showToastMessage('Asset missing file key', 'error');
            return;
        }
        addSlide({ preview: asset.url || '', fileKey: asset.file_key });
        setPickerOpen(false);
    };

    const pickProject = (p: { _id: string; title?: string; thumbnail?: string; feature_image?: string }) => {
        setSlides([]);
        setPreviewIdx(0);
        setProject({ id: p._id, preview: p.thumbnail || p.feature_image || '', title: p.title || '' });
        setYt(prev => ({ ...prev, title: prev.title || p.title || '' }));
        setPickerOpen(false);
    };

    const reset = () => {
        setCaption('');
        setSchedDate(null);
        setSchedTime(null);
        clearMedia();
        setYt(defaultYoutubeFields());
        setTt(defaultTiktokFields());
        setUploading(false);
    };

    const submitWithMode = async (publishNow: boolean) => {
        if (!hasMedia) return;
        if (publishNow && scheduledAt && new Date(scheduledAt) > new Date()) {
            setPostNowConfirmOpen(true);
            return;
        }
        const resolvedScheduledAt = publishNow ? new Date().toISOString() : scheduledAt;
        if (!publishNow && !resolvedScheduledAt) return void showToastMessage('Schedule time is required', 'error');
        const invalid = validatePlatformFields(platform, yt, tt);
        if (invalid) return void showToastMessage(invalid, 'error');
        setUploading(true);
        try {
            const keys: string[] = [];
            for (const slide of slides) {
                if (slide.fileKey) {
                    keys.push(slide.fileKey);
                    continue;
                }
                if (!slide.file) throw new Error('Missing media file');
                const result = (await dispatchP(
                    uploadUserMediaLibraryImage({ file: slide.file, category: 'other', name: slide.file.name }),
                )) as IUploadUserMediaLibraryImageResult;
                const key = result?.asset?.file_key;
                if (!key) throw new Error('Upload did not return a file key');
                keys.push(key);
            }
            await onSubmit?.({
                platform,
                format,
                mediaType: mediaTypeFor(format, keys.length),
                aspect,
                caption,
                ...buildPlatformFields(platform, caption, yt, tt),
                projectId: project?.id,
                scheduledAt: resolvedScheduledAt!,
                mediaUrls: keys,
                publishNow,
            });
            reset();
            onClose();
        } catch (err: any) {
            showToastMessage(err?.message || 'Could not upload media', 'error');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={uploading ? undefined : onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                    backgroundImage: 'none',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.06)',
                    maxHeight: '92vh',
                },
            }}>
            <Box
                sx={{
                    px: 2.5,
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: BRAND_SURFACE_CONTAINER,
                }}>
                <Box>
                    <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 16 }}>
                        Compose {PLATFORM_LABEL[platform]}
                    </Typography>
                    <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12 }}>
                        Instagram, TikTok, or YouTube · media library or project · phone preview
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={uploading} sx={{ color: BRAND_LUXE_MUTED }}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <DialogContent sx={{ p: 0 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 0.8fr' }, minHeight: 480 }}>
                    <Box sx={{ p: 2.5, borderRight: { md: '1px solid rgba(255,255,255,0.06)' }, overflow: 'auto' }}>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            {PLATFORMS.map(p => (
                                <Box
                                    key={p}
                                    component="button"
                                    disabled={uploading}
                                    onClick={() => changePlatform(p)}
                                    sx={chipSx(platform === p)}>
                                    {PLATFORM_LABEL[p]}
                                </Box>
                            ))}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                            {PLATFORM_FORMATS[platform].map(f => (
                                <Box
                                    key={f.id}
                                    component="button"
                                    disabled={uploading}
                                    onClick={() => changeFormat(f.id)}
                                    sx={chipSx(format === f.id)}>
                                    {f.label}
                                    {f.id === 'carousel' ? ` (${slides.length}/10)` : null}
                                </Box>
                            ))}
                            <TextField
                                select
                                size="small"
                                value={aspect}
                                disabled={uploading}
                                onChange={e => setAspect(e.target.value as ComposeAspect)}
                                sx={{ ...brandModalOutlinedInputSx, width: 110, ml: 'auto' }}>
                                {(['4:5', '1:1', '9:16'] as ComposeAspect[]).map(a => (
                                    <MenuItem key={a} value={a}>
                                        {a}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>

                        <Box
                            onDragOver={e => {
                                e.preventDefault();
                                setDragging(true);
                            }}
                            onDragLeave={() => setDragging(false)}
                            onDrop={e => {
                                e.preventDefault();
                                setDragging(false);
                                if (!uploading) onFiles(e.dataTransfer.files);
                            }}
                            sx={{
                                border: `1px dashed ${dragging ? BRAND_LUXE_PRIMARY : 'rgba(255,255,255,0.15)'}`,
                                borderRadius: 2,
                                p: 2,
                                mb: 2,
                                textAlign: 'center',
                                bgcolor: BRAND_SURFACE_CONTAINER,
                            }}>
                            <AddPhotoAlternateOutlinedIcon sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 28, mb: 0.5 }} />
                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mb: 1 }}>
                                {isVideo
                                    ? 'Drop a cover image, or pick a video from Library / Projects'
                                    : 'Drag & drop images, upload, or pick from Library / Projects'}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                                <ManageGoldButton component="label" disabled={uploading}>
                                    Choose images
                                    <input
                                        hidden
                                        type="file"
                                        accept="image/*"
                                        multiple={format === 'carousel'}
                                        onChange={e => onFiles(e.target.files)}
                                    />
                                </ManageGoldButton>
                                <ManageGoldButton
                                    disabled={uploading}
                                    onClick={() => setPickerOpen(true)}
                                    sx={{
                                        bgcolor: 'transparent',
                                        color: BRAND_LUXE_PRIMARY,
                                        border: '1px solid rgba(255,201,101,0.35)',
                                    }}>
                                    <PhotoLibraryOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                                    Library / Projects
                                </ManageGoldButton>
                            </Box>
                            {project ? (
                                <Typography sx={{ color: BRAND_LUXE_PRIMARY, fontSize: 12, mt: 1.5 }}>
                                    Project: {project.title || project.id}
                                </Typography>
                            ) : null}
                            {slides.length ? (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        gap: 0.75,
                                        mt: 1.5,
                                        flexWrap: 'wrap',
                                        justifyContent: 'center',
                                    }}>
                                    {slides.map((slide, i) => (
                                        <Box
                                            key={i}
                                            component="img"
                                            src={slide.preview}
                                            onClick={() => setPreviewIdx(i)}
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                objectFit: 'cover',
                                                borderRadius: 1,
                                                border:
                                                    previewIdx === i
                                                        ? `2px solid ${BRAND_LUXE_PRIMARY}`
                                                        : '2px solid transparent',
                                                cursor: 'pointer',
                                            }}
                                        />
                                    ))}
                                </Box>
                            ) : null}
                        </Box>

                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            label="Caption"
                            value={caption}
                            disabled={uploading}
                            onChange={e => setCaption(e.target.value)}
                            sx={{ ...brandModalOutlinedInputSx, mb: 1.5 }}
                        />
                        <PlatformComposeFields
                            platform={platform}
                            sharedCaption={caption}
                            youtube={yt}
                            onYoutubeChange={patch => setYt(prev => ({ ...prev, ...patch }))}
                            tiktok={tt}
                            onTiktokChange={patch => setTt(prev => ({ ...prev, ...patch }))}
                            tiktokActive={open && platform === 'tiktok'}
                            tiktokMediaPreviewUrl={previews[0]}
                            tiktokCreatorNickname={activeBrand?.name}
                        />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 12, mb: 0.75, mt: 2 }}>
                                Schedule
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                                <DatePicker
                                    label="Date"
                                    value={schedDate}
                                    disabled={uploading}
                                    onChange={v => setSchedDate(v)}
                                    slotProps={{
                                        textField: { size: 'small', fullWidth: true, sx: brandModalOutlinedInputSx },
                                        popper: { sx: { zIndex: 9999 } },
                                        desktopPaper: { sx: pickerPaperSx },
                                        day: { sx: pickerDaySx },
                                        calendarHeader: { sx: { color: '#fff' } },
                                        switchViewButton: { sx: { color: '#fff' } },
                                        previousIconButton: { sx: { color: BRAND_LUXE_MUTED } },
                                        nextIconButton: { sx: { color: BRAND_LUXE_MUTED } },
                                        yearButton: { sx: pickerDaySx },
                                        actionBar: { sx: pickerActionBarSx },
                                    }}
                                />
                                <TimePicker
                                    label="Time"
                                    value={schedTime}
                                    disabled={uploading}
                                    onChange={v => setSchedTime(v)}
                                    slotProps={{
                                        textField: { size: 'small', fullWidth: true, sx: brandModalOutlinedInputSx },
                                        popper: { sx: { zIndex: 9999 } },
                                        desktopPaper: { sx: pickerPaperSx },
                                        digitalClockSectionItem: { sx: pickerTimeItemSx },
                                        actionBar: { sx: pickerActionBarSx },
                                    }}
                                />
                            </Box>
                        </LocalizationProvider>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                            <ManageGoldButton onClick={() => submitWithMode(false)} disabled={!hasMedia || uploading}>
                                {uploading ? 'Uploading…' : 'Add to calendar'}
                            </ManageGoldButton>
                            {canPostNow && (
                                <ManageGoldButton
                                    onClick={() => submitWithMode(true)}
                                    disabled={!hasMedia || uploading}
                                    sx={{
                                        bgcolor: 'rgba(231,132,16,0.15)',
                                        border: '1px solid rgba(231,132,16,0.5)',
                                        color: BRAND_LUXE_PRIMARY,
                                        '&:hover': {
                                            bgcolor: 'rgba(231,132,16,0.25)',
                                            border: '1px solid rgba(231,132,16,0.8)',
                                        },
                                    }}>
                                    Post now
                                </ManageGoldButton>
                            )}
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            p: 3,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#0a0a0a',
                        }}>
                        <ComposePhonePreview
                            platform={platform}
                            aspect={aspect}
                            isVideo={isVideo}
                            previews={previews}
                            previewIdx={previewIdx}
                            onSelectIdx={setPreviewIdx}
                            caption={caption}
                            showDots={format === 'carousel' && previews.length > 1}
                        />
                    </Box>
                </Box>
            </DialogContent>

            <ComposeMediaPicker
                open={pickerOpen}
                onClose={() => setPickerOpen(false)}
                brandId={activeBrand?._id ? String(activeBrand._id) : undefined}
                projectType={isVideo ? 'video' : 'picture'}
                onPickAsset={pickAsset}
                onPickProject={pickProject}
            />

            {/* Post now confirmation when a future date is already selected */}
            <Dialog
                open={postNowConfirmOpen}
                onClose={() => setPostNowConfirmOpen(false)}
                PaperProps={{ sx: { bgcolor: '#131313', borderRadius: 2, p: 3, maxWidth: 380 } }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', fontSize: 15, mb: 1 }}>
                    Post immediately?
                </Typography>
                <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13, lineHeight: 1.6, mb: 3 }}>
                    You've selected a scheduled time of{' '}
                    <Box component="span" sx={{ color: '#fff' }}>
                        {scheduledAt ? new Date(scheduledAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    </Box>
                    . Clicking <Box component="span" sx={{ color: BRAND_LUXE_PRIMARY }}>Post now</Box> will publish immediately and ignore that time.
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <ManageGoldButton
                        sx={{ flex: 1, bgcolor: 'transparent', border: '1px solid rgba(255,255,255,0.12)', color: BRAND_LUXE_MUTED }}
                        onClick={() => setPostNowConfirmOpen(false)}>
                        Cancel
                    </ManageGoldButton>
                    <ManageGoldButton
                        sx={{ flex: 1 }}
                        onClick={() => {
                            setPostNowConfirmOpen(false);
                            submitWithMode(true);
                        }}>
                        Post now anyway
                    </ManageGoldButton>
                </Box>
            </Dialog>
        </Dialog>
    );
}
